/**
 * «¿Qué entró?» — la primera pregunta del Ingreso de Inventario.
 *
 * Un iPhone no se describe como el resto: el usado es UNO, con batería e IMEI,
 * y el sellado se reparte por color. Todo lo demás se busca por nombre y se le
 * suman unidades. Preguntarlo primero manda a cada cosa por su camino, en vez
 * de un formulario que sirve para todo y guía para nada.
 *
 * El usado va primero porque es lo que más entra por acá (los sellados se
 * venden a pedido y la mercadería con remito entra por Compras).
 */
import { Package, PackageCheck, Smartphone, type LucideIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export type QueEntro = "usado" | "sellado" | "otro";

const OPCIONES: ReadonlyArray<{
  que: QueEntro;
  titulo: string;
  detalle: string;
  icono: LucideIcon;
}> = [
  {
    que: "usado",
    titulo: "iPhone usado",
    detalle: "De a uno, con batería e IMEI.",
    icono: Smartphone,
  },
  {
    que: "sellado",
    titulo: "iPhone sellado",
    detalle: "Nuevo, en caja: modelo, color y cantidad.",
    icono: PackageCheck,
  },
  {
    que: "otro",
    titulo: "Otra cosa",
    detalle: "Accesorios, perfumes, vapers y el resto.",
    icono: Package,
  },
];

const CLASE_OPCION = "h-auto w-full justify-start whitespace-normal px-4 py-3 text-left";

export interface DialogQueEntroProps {
  abierto: boolean;
  onAbiertoChange: (abierto: boolean) => void;
  onElegir: (que: QueEntro) => void;
}

export function DialogQueEntro({
  abierto,
  onAbiertoChange,
  onElegir,
}: DialogQueEntroProps): JSX.Element {
  return (
    <Dialog open={abierto} onOpenChange={onAbiertoChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>¿Qué entró?</DialogTitle>
          <DialogDescription>Elegí qué es y te pregunta solo lo que hace falta.</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div role="group" aria-label="¿Qué entró?" className="flex flex-col gap-2">
            {/* Tarjetas-opción: elegir avanza, por eso no hay ninguna marcada. */}
            {OPCIONES.map(({ que, titulo, detalle, icono: Icono }) => (
              <Button
                key={que}
                type="button"
                variant="outline"
                onClick={() => onElegir(que)}
                className={CLASE_OPCION}
              >
                <Icono className="text-muted-foreground" aria-hidden="true" />
                <span className="min-w-0">
                  <span className="block text-sm font-medium">{titulo}</span>
                  <span className="block text-xs font-normal text-muted-foreground">{detalle}</span>
                </span>
              </Button>
            ))}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onAbiertoChange(false)}>
            Cancelar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
